import React, { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { SoftwareEngineering } from '../../utils/formNames';
import { getFormData } from '../../utils/sessionStorage';
import { FORM_HOST } from '../../utils/constants';
import EnrollmentForm from './EnrollmentForm';
import EnrollmentFormRadioBlockInput from '../FormInputs/FormRadioBlockInput';
import EnrollmentFormTextArea from '../FormInputs/FormTextArea';
import EnrollmentFormError from '../FormInputs/FormError';

const formName = SoftwareEngineering;
const pardotFormUrl = `${FORM_HOST}/l/772833/2020-04-14/5c6y96`;
const callbackLocations = {
  success_location: `${FORM_HOST}/l/772833/2020-07-06/7k97ps/772833/58621/software_engineering_success.js`,
  error_location: `${FORM_HOST}/l/772833/2020-07-06/7k97pq/772833/58619/software_engineering_error.js`,
};

const SoftwareEngineeringForm = () => {
  const { register, handleSubmit, triggerValidation, formState, errors } = useForm({
    mode: 'onChange',
    submitFocusError: false,
  });

  const previousData = getFormData(formName);

  // Necessary because react-hook-form has a bug
  // regarding default values on required radio inputs
  useEffect(() => {
    if (previousData.familiar_with_algos) {
      triggerValidation();
    }
  }, [previousData.familiar_with_algos, triggerValidation]);

  // Allow tabbing inside coding textarea
  useEffect(() => {
    const codeArea = document.getElementById('algos_quiz');
    const tabAddSpaces = (event) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        const start = codeArea.selectionStart;
        const end = codeArea.selectionEnd;
        codeArea.value = `${codeArea.value.substring(0, start)}  ${codeArea.value.substring(end)}`;
        codeArea.selectionStart = start + 2;
        codeArea.selectionEnd = codeArea.selectionStart;
      }
    };
    codeArea.addEventListener('keydown', tabAddSpaces);
    return () => {
      codeArea.removeEventListener('keydown', tabAddSpaces);
    };
  }, []);

  const timer = useRef(null);
  const debounceTriggerValidation = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(triggerValidation, 600);
  };

  return (
    <EnrollmentForm
      formName={formName}
      callbackLocations={callbackLocations}
      actionUrl={pardotFormUrl}
      formValid={formState.isValid}
      validationHandler={handleSubmit}
    >
      <legend>Software Engineering</legend>
      <p>
        How many total problems have you completed on Leetcode, Hackerrank, Codewar, or other similar platforms?{' '}
        <em>*</em>
      </p>
      <div className="blockInputs">
        <EnrollmentFormRadioBlockInput
          name="completed_coding_practice_questions"
          id="less_than_100"
          label="< 100"
          value="< 100"
          defaultValue={previousData.completed_coding_practice_questions}
          inputRef={register({ required: true })}
        />
        <EnrollmentFormRadioBlockInput
          name="completed_coding_practice_questions"
          id="100_to_200"
          label="100 - 200"
          value="100 - 200"
          defaultValue={previousData.completed_coding_practice_questions}
          inputRef={register({ required: true })}
        />
        <EnrollmentFormRadioBlockInput
          name="completed_coding_practice_questions"
          id="200_to_300"
          label="200 - 300"
          value="200 - 300"
          defaultValue={previousData.completed_coding_practice_questions}
          inputRef={register({ required: true })}
        />
        <EnrollmentFormRadioBlockInput
          name="completed_coding_practice_questions"
          id="greater_than_300"
          label="> 300"
          value="> 300"
          defaultValue={previousData.completed_coding_practice_questions}
          inputRef={register({ required: true })}
        />
      </div>
      <EnrollmentFormError errorField={errors.familiar_with_algos} blockError />
      <EnrollmentFormTextArea
        id="algos_quiz"
        name="algos_quiz"
        className="codeTextBox"
        label="Using your favorite language, find the kth largest element in an unsorted array. Note that it is the kth largest element in the sorted order, not the kth distinct element. What is a second way you might do the same problem? Which approach would you prefer and why? (Optional)"
        placeholder="Your code here"
        defaultValue={previousData.algos_quiz}
        rows={8}
        inputRef={register}
        error={errors.algos_quiz}
        onChange={debounceTriggerValidation}
      />
      <EnrollmentFormError
        errorField={errors.algos_quiz}
        errorText={{
          minLength: 'Please be sure to fully answer this question.',
        }}
      />
    </EnrollmentForm>
  );
};

export default SoftwareEngineeringForm;
